import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabaseClient';
import { InvestmentAccount } from '../types';
import { useAuth } from '../../auth/context/AuthContext';

/**
 * useAccount
 * 
 * React Query hook for fetching a single account directly from Supabase.
 * Used for the edit account modal to ensure we always have the latest
 * account name, current_value, and current_value_updated_at from the database.
 * 
 * @param accountId - The account ID to fetch
 * @returns React Query result with account data
 */
export function useAccount(accountId?: string) {
  const { user } = useAuth();

  return useQuery({
    enabled: !!accountId && !!user?.id,
    queryKey: ['account', accountId],
    queryFn: async (): Promise<InvestmentAccount | null> => {
      if (!accountId || !user?.id) {
        throw new Error('Account ID and user ID are required');
      }

      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', accountId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      if (!data) return null;

      // Map database fields to InvestmentAccount format
      return {
        id: data.id,
        accountName: data.account_name,
        company: data.company ?? '',
        logoUrl: data.logo_url,
        transactions: [], // Transactions loaded separately via useAccountTransactions
        createdAt: data.created_at ?? '',
        isHidden: !!data.is_hidden,
        isManuallyEdited: !!data.is_manually_edited,
        current_value: data.current_value ?? null,
        current_value_updated_at: data.current_value_updated_at ?? null,
        brokerageAccountId: data.brokerage_account_id ?? null,
        brokerageConnectionId: data.brokerage_connection_id ?? null,
        accountStatus: data.account_status ?? null,
        lastSyncTime: data.last_sync_time ?? null,
        isSnapTradeLinked: Boolean(data.brokerage_account_id),
        type: '',
        balance: 0,
        user_id: data.user_id,
      };
    },
    staleTime: 30_000,
  });
}
